import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { DragEvent, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CompanySelect } from 'src/components/CompanySelect/CompanySelect'
import { useGlobalSelectedCompany } from 'src/context/GlobalSelectedCompanyContext'
import { getRole } from 'src/service/authService'

import {
  ACCEPTED_FILE_EXTENSIONS,
  formatFileSize,
  getMaxSizeMB,
  validateFile,
} from '../utils/fileHelpers'

interface UploadFileModalProps {
  open: boolean
  onClose: () => void
  onUpload: (file: File, description?: string, companyId?: string) => Promise<any>
}

export function UploadFileModal({
  open,
  onClose,
  onUpload,
}: UploadFileModalProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  const isTecnoit = getRole() === 'TECNOIT'
  const { isGlobalAdminSelected, selectedCompanyData } = useGlobalSelectedCompany()

  // Track selected company separately for reliable submit
  const [selectedCompany, setSelectedCompany] = useState<any>(null)

  // useForm only needed to provide `control` for CompanySelect
  const { control, setValue } = useForm({
    defaultValues: { companyId: undefined as any },
  })

  // Pre-fill from sidebar selection when modal opens
  useEffect(() => {
    if (isTecnoit && open) {
      if (!isGlobalAdminSelected() && selectedCompanyData) {
        setValue('companyId', selectedCompanyData)
        setSelectedCompany(selectedCompanyData)
      } else {
        setValue('companyId', undefined)
        setSelectedCompany(null)
      }
    }
  }, [open, selectedCompanyData])

  const handleCompanyChange = (company: any) => {
    setSelectedCompany(company)
  }

  const processFile = (file: File) => {
    const validationError = validateFile(file)
    setError(validationError)
    setSelectedFile(file)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) processFile(file)
  }

  const clearSelection = () => {
    setSelectedFile(null)
    setError(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleUpload = async () => {
    if (!selectedFile || error) return
    const companyId = isTecnoit && selectedCompany
      ? (selectedCompany.id || selectedCompany.companyId)
      : undefined
    setIsUploading(true)
    const res = await onUpload(selectedFile, description.trim() || undefined, companyId)
    setIsUploading(false)
    if (res) {
      handleClose()
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setDescription('')
    setError(null)
    setIsDragging(false)
    setIsUploading(false)
    setValue('companyId', undefined)
    setSelectedCompany(null)
    if (inputRef.current) inputRef.current.value = ''
    onClose()
  }

  const isUploadDisabled =
    !selectedFile || !!error || isUploading || (isTecnoit && !selectedCompany)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('fileRepository.modal.upload.title')}</DialogTitle>
      <DialogContent>
        {isTecnoit && (
          <CompanySelect
            sx={{ mt: 1, gridColumn: 'span 12' }}
            control={control}
            onChangeAction={handleCompanyChange}
          />
        )}

        {/* Drop zone */}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: '2.5px dashed rgba(0, 0, 0, 0.38)',
            borderRadius: '8px',
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            mt: 2,
            ':hover': { borderColor: '#FF5E1E', color: '#FF5E1E' },
            ...(isDragging && {
              borderColor: '#FF5E1E',
              backgroundColor: 'rgba(255, 94, 30, 0.08)',
            }),
          }}
        >
          <CloudUploadOutlined sx={{ fontSize: 48, mb: 1 }} />
          <Typography>{t('fileRepository.modal.upload.dragDrop')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('fileRepository.modal.upload.allowedTypes')}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            {t('fileRepository.modal.upload.maxSizeImage')}
          </Typography>
          <input
            ref={inputRef}
            type="file"
            hidden
            accept={ACCEPTED_FILE_EXTENSIONS}
            onChange={handleFileSelect}
          />
        </Box>

        {/* Selected file preview */}
        {selectedFile && (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              backgroundColor: error
                ? 'rgba(211, 47, 47, 0.08)'
                : 'rgba(255, 94, 30, 0.08)',
              borderRadius: '4px',
            }}
          >
            {!error && <CheckCircleIcon sx={{ color: '#FF5E1E' }} />}
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedFile.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({formatFileSize(selectedFile.size)})
            </Typography>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); clearSelection() }}>
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Validation error */}
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {t(error, { limit: selectedFile ? getMaxSizeMB(selectedFile) : 5 })}
          </Typography>
        )}

        {/* Optional description */}
        <TextField
          fullWidth
          label={t('fileRepository.modal.upload.descriptionLabel')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={2}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('general.button.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={isUploadDisabled}
        >
          {isUploading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t('fileRepository.modal.upload.button')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
